































































































import axios from 'axios';
import CoText from '../../Atoms/co-text/CoText.vue';
import CoCheckbox from '../../Atoms/co-checkbox/CoCheckbox.vue';
import CoInput from '../co-input/CoInput.vue';
import CoThumbnail from '../co-thumbnail/CoThumbnail.vue';

interface Item {
    Name: string;
    Value: string | number | boolean | null | undefined | object;
    ShowThumbnail?: boolean; // Whether to show thumbnail
    ThumbnailImageURL?: string; // Optional image URL
    SecondaryThumbnailImageURL?: string; // Optional secondary image URL displayed as small icon
    RoundThumbnail?: boolean; // Use round thumbnail style
    Disabled: boolean;
}

export default {
    name: 'CoMultiSelect',
    components: { CoText, CoCheckbox, CoInput, CoThumbnail },
    props: {
        value: {
            type: Array,
            default: () => [] as Item[],
        },
        items: {
            type: Array,
            default: () => [] as Item[],
        },
        placeholder: {
            type: String,
            default: 'Select',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        selectFirstItem: {
            type: Boolean,
            default: false,
        },
        searchable: {
            type: Boolean,
            default: true,
        },
        searchFn: {
            type: Function,
            default: null, // (query, items) => filteredItems
        },
        searchPlaceholder: {
            type: String,
            default: 'Search...',
        },
        noOptionsLabel: {
            type: String,
            default: '',
        },
        showSelectAll: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            selectOpen: false,
            searchQuery: '',
            searchResults: [] as Item[],
            searchLoading: false,
            selectedItems: ([] as Item[]).concat(this.value || []),
            source: null,
        };
    },
    computed: {
        classes() {
            return {
                expanded: this.selectOpen,
                disabled: this.disabled,
            };
        },
        filteredItems(): Item[] {
            if (this.searchable && this.searchFn) {
                if (this.searchQuery) {
                    let result = this.searchResults;

                    if (this.selectedItems && this.selectedItems.length > 0) {
                        this.selectedItems.forEach((item) => {
                            if (!result.some((r) => r.Value === item.Value)) {
                                result.push(item);
                            }
                        });
                    }

                    return result;
                }

                if (this.selectedItems && this.selectedItems.length > 0) {
                    // If no search query, return selected items
                    return this.selectedItems;
                }
                // If no search query, return search results directly
                return [];
            }
            const q = this.searchQuery.toLowerCase();
            const itemsOutput = this.items?.filter((item: Item) => (item.Name || '').toLowerCase().includes(q));

            return itemsOutput;
        },
        allSelected(): boolean {
            if (this.searchResults.length === 0 && this.items.length === 0) return false;
            const enabled = this.filteredItems?.filter((item) => !item.Disabled);
            return enabled.length > 0 && enabled.every((item) => this.isSelected(item));
        },
        allDisabled(): boolean {
            return this.filteredItems?.every((item) => item.Disabled);
        },
        visibleSelected(): Item[] {
            // Show 1-2 selected items if fit, rest as +n
            const maxLabels = 2;
            return this.selectedItems.slice(0, maxLabels);
        },
    },
    watch: {
        value(newVal) {
            this.selectedItems = ([] as Item[]).concat(newVal || []);
        },
        items(newItems) {
            // Remove deselected items if not in new list
            this.selectedItems = this.selectedItems.filter((sel) => newItems.some((i) => i.Value === sel.Value));
        },
        searchQuery(val) {
            if (!this.searchable || !this.searchFn) return;
            this.search(val);
        },
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    destroyed() {
        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
        async search(input: string) {
            this.searchLoading = true;
            if (!this.searchable || !this.searchFn) {
                this.searchResults = [];
            }

            // Cancel previous search if exists
            if (this.source) {
                this.source.cancel('Operation canceled by the user.');
            }
            const query = (input || '').toLowerCase();

            try {
                this.source = axios.CancelToken.source();
                const results = await this.searchFn(query, this.source.token);
                this.searchResults = results || [];
                this.searchLoading = false;
            } catch (error) {
                console.error('Search function error:', error);
                if (axios.isCancel(error)) {
                    console.warn('Search request canceled:', error.message);
                } else {
                    this.$emit('error', error);
                }
                this.searchResults = [];
                this.searchLoading = false;
            }
        },
        toggleDropdown() {
            if (this.disabled) return;
            this.selectOpen = !this.selectOpen;
            if (this.selectOpen) {
                this.searchQuery = '';
                this.searchResults = [];
                this.searchLoading = false;
            }
        },
        isSelected(item: Item) {
            return this.selectedItems.some((sel) => sel.Value === item.Value);
        },
        toggleItem(item: Item) {
            if (item.Disabled) return;
            let arr = this.selectedItems.slice();
            if (this.isSelected(item)) {
                arr = arr.filter((sel) => sel.Value !== item.Value);
            } else {
                arr.push(item);
            }
            this.selectedItems = arr;
            this.$emit('input', arr);
            this.$emit('select', arr);
        },
        toggleSelectAll(val) {
            if (this.allDisabled) return;
            if (this.allSelected) {
                // Deselect all enabled
                const disabledVals = this.selectedItems?.filter((i) => i.Disabled);
                this.selectedItems = disabledVals;
            } else {
                // Select all enabled
                const enabled = this.filteredItems?.filter((i) => !i.Disabled);
                const disabled = this.selectedItems?.filter((i) => i.Disabled);
                this.selectedItems = [...enabled, ...disabled];
            }
            this.$emit('input', this.selectedItems);
            this.$emit('select', this.selectedItems);
        },
        onSearch(val: string) {
            this.searchQuery = val;
        },
        handleClickOutside(event: MouseEvent) {
            if (!this.$el.contains(event.target as Node) && this.selectOpen) {
                this.selectOpen = false;
            }
        },
    },
};
